import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, HeartIcon } from 'lucide-react';
import { ButtonLink } from '../ui/ButtonLink';
import { FitBadge } from '../ui/FitBadge';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { images } from '../../data/images';
import { useLanguage } from '../../contexts/LanguageContext';
import { rise, spring, stagger } from '../../utils/motion';
export function Hero() {
  const ref = useRef<HTMLDivElement>(null);
  const {
    t
  } = useLanguage();
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const yRaw = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const y = reduce || !isDesktop ? 0 : yRaw;
  return <section ref={ref} aria-labelledby="hero-title" className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-14 lg:min-h-[86vh] lg:grid-cols-[1.1fr_1fr] lg:pt-10" data-id="element-345" data-mp="3u010rrnhgajo">
      <motion.div variants={stagger} initial="hidden" animate="show" data-id="element-346" data-mp="pwxoszgwa6sm">
        <motion.h1 variants={rise} id="hero-title" className="text-[44px] font-bold leading-[1.0] tracking-[-0.02em] sm:text-[64px] lg:text-[88px]" data-id="element-347" data-mp="pu35xcdj4u1b">
          {t('hero.line1')}
          <br data-id="element-348" data-mp="14lkf1w828iym" />
          <span className="text-brand-strong" data-id="element-349" data-mp="14l29g8zicse1">{t('hero.line2')}</span>
        </motion.h1>
        <motion.p variants={rise} className="mt-6 max-w-[46ch] text-[18px] font-medium leading-relaxed text-ink-2 lg:text-[20px]" data-id="element-350" data-mp="19kx0ccja9hz3">
          {t('hero.lead')}
        </motion.p>
        <motion.div variants={rise} className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-351" data-mp="utoco8yxy6ol">
          <ButtonLink to="/onboarding/performer" variant="accent" size="lg" className="w-full sm:w-auto" data-id="element-352" data-mp="1nk6fr9zo0820">
            {t('hero.ctaPerform')}
          </ButtonLink>
          <Link to="/onboarding/organiser" className="group inline-flex items-center justify-center gap-1.5 px-2 py-3 font-bold text-ink" data-id="element-353" data-mp="1schewamkzpti">
            {t('hero.ctaOrganise')}
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-1" data-id="element-354" data-mp="1x8txcu888ryi" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div style={{
      y
    }} className="relative mx-auto h-[460px] w-full max-w-[400px] will-change-transform lg:h-[540px]" data-id="element-355" data-mp="1o7dwpe77s47h">
        <motion.img src={images.funkBand} alt="" className="absolute left-0 top-8 h-[78%] w-[72%] rounded-lg object-cover" initial={{
        opacity: 0,
        rotate: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        rotate: -8,
        scale: 1
      }} transition={{
        ...spring.heavy,
        delay: 0.15
      }} data-id="element-356" data-mp="1j92mv3gx5tp9" />
        <motion.img src={images.jazzTrio} alt="" className="absolute right-0 top-4 h-[78%] w-[72%] rounded-lg object-cover" initial={{
        opacity: 0,
        rotate: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        rotate: 7,
        scale: 1
      }} transition={{
        ...spring.heavy,
        delay: 0.2
      }} data-id="element-357" data-mp="14ky11qmkl229" />
        <motion.div className="absolute inset-x-[10%] bottom-0 top-0 overflow-hidden rounded-lg bg-surface-2 shadow-[0_24px_60px_rgb(0_0_0/0.25)]" initial={{
        opacity: 0,
        y: 24,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} transition={{
        ...spring.bouncy,
        delay: 0.3
      }} data-id="element-358" data-mp="3c4mrlddtjq09">
          <img src={images.ploy} alt="Ploy Siriwan, soul singer, performing" className="h-full w-full object-cover" data-id="element-359" data-mp="2ogbxct2mooky" />
          <div className="absolute inset-x-3 bottom-3 rounded-md bg-surface/95 p-4 backdrop-blur-md" data-id="element-360" data-mp="2shfbnb512ck2">
            <p className="flex items-center gap-1.5 text-xl font-bold" data-id="element-361" data-mp="2xefkftt9htev">
              Ploy Siriwan <BadgeCheckIcon className="h-5 w-5 text-brand-strong" aria-label="Verified" data-id="element-362" data-mp="327h1o8gxy51m" />
            </p>
            <p className="text-sm text-ink-2" data-id="element-363" data-mp="24qs26lfgreve">Soul singer · Ari, Bangkok · from ฿9,000</p>
          </div>
        </motion.div>
        <motion.div className="absolute right-[4%] top-[14%]" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        ...spring.bouncy,
        delay: 0.6
      }} data-id="element-364" data-mp="29obehfslphkp">
          <FitBadge score={94} className="h-9 px-4 text-sm" data-id="element-365" data-mp="bafbrvjn5zxz" />
        </motion.div>
        <motion.div className="absolute -left-2 top-[46%] flex items-center gap-2 rounded-full bg-accent px-4 py-2.5 text-sm font-bold text-on-accent shadow-lg" initial={{
        opacity: 0,
        scale: 0.96,
        x: -10
      }} animate={{
        opacity: 1,
        scale: 1,
        x: 0
      }} transition={{
        ...spring.bouncy,
        delay: 0.75
      }} data-id="element-366" data-mp="p0ski04cxiod">
          <HeartIcon className="h-4 w-4" fill="currentColor" data-id="element-367" data-mp="17yv23tuksdc5" /> New match · Rooftop Soul
        </motion.div>
      </motion.div>
    </section>;
}